/**
 * Helpers for working with gobodo-api-promise `*WithHttpInfo` methods.
 *
 * Each SDK method has two variants:
 *   method(opts)                 → data
 *   methodWithHttpInfo(opts)     → { data, response }
 *
 * The WithHttpInfo form exposes response headers (x-request-id, cache flags,
 * etc.) even on success. Use {@link callWithHttpInfo} when a call is deep in
 * a chain and you want the request ID logged for traceability — it returns
 * just the data so call sites don't change shape, but on the way through
 * it records the request ID via the provided callback (default: console.debug).
 */

import { extractRequestId } from './gobodoErrorHandler';

export interface WithHttpInfoResult<T> {
  data: T;
  response: {
    status?: number;
    headers?: Record<string, string>;
    [k: string]: any;
  };
}

export interface CallWithHttpInfoOptions {
  /** Operation label used in the log line. */
  operation: string;
  /** Where to send the debug line. Defaults to a no-op in production, console.debug in dev. */
  onTrace?: (line: string) => void;
}

const DEFAULT_ON_TRACE =
  process.env.NODE_ENV === 'production'
    ? () => { /* silent */ }
    : (line: string) => console.debug(line);

/**
 * Invoke a `*WithHttpInfo` call, log the request ID + status once the
 * response lands, and return just the `data`. Errors are re-thrown unchanged
 * (the SDK error object already carries the response headers, which
 * {@link extractRequestId} can reach).
 */
export async function callWithHttpInfo<T>(
  call: () => Promise<WithHttpInfoResult<T>>,
  options: CallWithHttpInfoOptions
): Promise<T> {
  const onTrace = options.onTrace ?? DEFAULT_ON_TRACE;
  const result = await call();
  const requestId = extractRequestId(result.response);
  const status = result.response?.status;
  onTrace(
    `[Gobodo] ${options.operation} ok` +
      (status != null ? ` status=${status}` : '') +
      (requestId ? ` requestId=${requestId}` : '')
  );
  return result.data;
}
